import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Claim, ClaimType, SchemeParams } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const CLAIM_TYPES: Array<{ value: ClaimType; label: string }> = [
  { value: 'RETIREMENT', label: 'Retirement' },
  { value: 'INCAPACITY', label: 'Incapacity' },
  { value: 'SURVIVING_SPOUSE', label: 'Surviving spouse' },
  { value: 'CHILDRENS', label: "Children's" },
  { value: 'DEPENDENT_ADULT', label: 'Dependent adult' },
];

export function MyBenefits() {
  const { persona } = useAuth();
  const claimsQ = useApiQuery<{ items: Claim[] }>('/api/v1/benefits/me/claims');
  const paramsQ = useApiQuery<SchemeParams>('/api/v1/benefits/parameters');
  const [showSubmit, setShowSubmit] = useState(false);

  return (
    <div>
      <PageHeader
        title="My Benefits"
        subtitle="Track your benefit claims and pension account"
        actions={
          <button className="btn-primary" onClick={() => setShowSubmit(true)}>
            + Submit a claim
          </button>
        }
      />

      {claimsQ.error ? (
        <div className="mb-4">
          <ErrorNote message={claimsQ.error} />
        </div>
      ) : null}
      <div className="card mb-6 overflow-x-auto">
        {claimsQ.loading ? (
          <Spinner />
        ) : (claimsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No benefit claims yet" hint="Submit a claim when you retire or need incapacity/survivor benefits." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reference</th>
                <th className="table-head">Type</th>
                <th className="table-head">Status</th>
                <th className="table-head text-right">Monthly amount</th>
                <th className="table-head">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {claimsQ.data!.items.map((c) => (
                <ClaimRow key={c.id} claim={c} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {paramsQ.data ? (
        <div className="card p-4">
          <h2 className="mb-2 text-sm font-bold text-slate-700">How your pension is calculated</h2>
          <p className="text-sm leading-relaxed text-slate-600">
            Your monthly pension is your average indexed monthly wage over your last{' '}
            <strong>{paramsQ.data.REFERENCE_YEARS}</strong> contributing years, multiplied by the{' '}
            <strong>{paramsQ.data.ACCRUAL_RATE_PCT}%</strong> accrual rate, multiplied by your years of contribution.
            Retiring before the pensionable age of <strong>{paramsQ.data.PENSIONABLE_AGE}</strong> reduces the
            pension by <strong>{paramsQ.data.EARLY_REDUCTION_PCT_PER_YEAR}%</strong> per year early. Surviving
            spouses receive <strong>{paramsQ.data.SURVIVOR_SHARE_PCT}%</strong> of the member's pension; children
            receive <strong>{paramsQ.data.CHILD_SHARE_PCT}%</strong> until age{' '}
            <strong>{paramsQ.data.CHILD_AGE_LIMIT}</strong>.
          </p>
        </div>
      ) : null}

      {showSubmit ? (
        <SubmitClaimModal
          nin={persona?.nin}
          onClose={() => setShowSubmit(false)}
          onDone={() => {
            setShowSubmit(false);
            claimsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ClaimRow({ claim }: { claim: Claim }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => !o)}>
        <td className="table-cell font-mono text-xs">{claim.refNumber}</td>
        <td className="table-cell">{claim.type.replaceAll('_', ' ')}</td>
        <td className="table-cell">
          <StatusBadge status={claim.status} />
        </td>
        <td className="table-cell text-right tabular-nums">
          {claim.account ? scr(claim.account.monthlyAmount) : claim.calc ? scr(claim.calc.monthlyAmount) : '—'}
        </td>
        <td className="table-cell text-slate-500">{formatDate(claim.createdAt)}</td>
      </tr>
      {open && claim.calc ? (
        <tr>
          <td colSpan={5} className="border-b border-slate-100 bg-slate-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Calculation breakdown</div>
            <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-slate-600">
              {claim.calc.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
      {open && claim.rejectReason ? (
        <tr>
          <td colSpan={5} className="border-b border-slate-100 bg-rose-50 px-4 py-3 text-xs text-rose-700">
            Rejected: {claim.rejectReason}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SubmitClaimModal({ nin, onClose, onDone }: { nin?: string; onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<ClaimType>('RETIREMENT');
  const [evidence, setEvidence] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!nin) {
      setError('No NIN on file for this session.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/benefits/claims', {
        nin,
        type,
        evidenceRefs: evidence
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Submit a benefit claim" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Claim type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as ClaimType)}>
            {CLAIM_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Supporting document references (optional, comma-separated)">
          <input
            className="field-input"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="e.g. DOCV-BIRTH-CERT-2026-004"
          />
        </Field>
        <p className="text-xs text-slate-400">
          An SPF benefits officer will verify your documents and run the calculation; your claim reference number is
          issued as soon as you submit.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Submitting…' : 'Submit claim'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
